// Pure builder for the Microsoft (Azure AD / Outlook) OAuth authorization request. Mirrors
// oauthRedirect.ts's GmailOAuthRedirect shape so the parameter mapping is unit-testable and both
// call sites (personal AddGmailAccount.tsx, organization OrganizationEmail/index.tsx) can share one
// implementation instead of each inlining their own params object.

export const MICROSOFT_OAUTH_ENDPOINT =
  "https://login.microsoftonline.com/common/oauth2/v2.0/authorize";

// Microsoft only allows redirect_uri values that are pre-registered (in the Azure AD app
// registration) for the OAuth client. The organization flow reuses the personal flow's already
// -registered `/callback/outlook` redirect_uri rather than requiring a second Azure AD redirect URI
// registration — this state value is how the shared callback route (OutlookAuthReturn.tsx) tells the
// two flows apart, mirroring ORG_GMAIL_OAUTH_STATE.
export const ORG_OUTLOOK_OAUTH_STATE = "org-email-outlook";

// Always show Microsoft's account chooser. Without a `prompt`, login.microsoftonline.com can silently reuse
// the Microsoft account the browser is already signed in with (and its prior consent) and redirect straight
// back to /callback/outlook -- so a brand-new landlord (or the next connect after a Disconnect) got linked to
// a DIFFERENT landlord's mailbox with no chance to choose. `select_account` forces the picker each time
// without forcing a re-consent for an account that already consented. It lets the user choose; it cannot
// stop them picking an account that belongs to someone else.
export const MICROSOFT_OAUTH_PROMPT = "select_account";

// Send-oriented scope set for the organization Outlook connection: send + a refresh token
// (offline_access) + a profile lookup (to resolve the connected address via Microsoft Graph `/me`).
// Organization-level Outlook stays send-only in Phase 2 (mirrors Gmail's own org-level connection,
// which is also send-only) — do not widen this constant for inbox-read scopes; that's what
// OUTLOOK_INBOX_SCOPES below is for, on the personal flow only.
export const OUTLOOK_SEND_SCOPES =
  "openid profile offline_access https://graph.microsoft.com/Mail.Send https://graph.microsoft.com/User.Read";

// Personal-flow scope set — Phase 2 adds full inbox parity (list/read/reply/forward/delete/star/
// counts/contacts) via Microsoft Graph, which needs Mail.ReadWrite (Mail.Send alone only covers
// sending) and People.Read (for GetTopContacts/SearchContacts via /me/people). Accounts connected
// under Phase 1's narrower OUTLOOK_SEND_SCOPES must reconnect to pick up these additional scopes —
// Graph rejects calls needing a scope the existing token wasn't consented for.
export const OUTLOOK_INBOX_SCOPES =
  "openid profile offline_access https://graph.microsoft.com/Mail.ReadWrite https://graph.microsoft.com/Mail.Send https://graph.microsoft.com/User.Read https://graph.microsoft.com/People.Read";

export interface MicrosoftOAuthRedirectInput {
  clientId: string;
  redirectUri: string;
  scopes: string;
  state?: string;
}

export interface MicrosoftOAuthRedirect {
  endpoint: string;
  params: Record<string, string>;
  authUrl: string;
}

export const buildMicrosoftOAuthRedirect = ({
  clientId,
  redirectUri,
  scopes,
  state = "pass-through value",
}: MicrosoftOAuthRedirectInput): MicrosoftOAuthRedirect => {
  const params: Record<string, string> = {
    client_id: `${clientId}`,
    redirect_uri: redirectUri,
    response_type: "code",
    scope: scopes,
    state,
    prompt: MICROSOFT_OAUTH_PROMPT,
  };

  const authUrl = `${MICROSOFT_OAUTH_ENDPOINT}?${new URLSearchParams(params).toString()}`;

  return { endpoint: MICROSOFT_OAUTH_ENDPOINT, params, authUrl };
};
